import { PayloadAction } from "@reduxjs/toolkit";
import {
  ICompareDocsItems,
  IIPDBillMappingFetchResp,
  IIPDBillsState,
  IIPDErrorResp,
  IIPDHealthCareBillHeaderFetchResp,
  IIPDSchemeBillPackageItem,
  IIPDSchemeHeaderFetchResp,
  ISchemeMappingsInHealthCare,
} from "../interfaces/IIPDBills";
import { ADD_IPD_BILL_WIZARD_ITEMS } from "./constants";
import { extractFileType } from "./globalUtils";
import { errorMessage, successMessage } from "./notificationUtils";
import Item from "antd/es/list/Item";

const updateSchemeDataUtil = (
  state: IIPDBillsState,
  action: PayloadAction<IIPDSchemeHeaderFetchResp | IIPDErrorResp>
) => {
  if (action.payload.response) {
    const schemeHeaderId = action.payload.response.schemeBillHeader.id;
    if (schemeHeaderId && action.payload.response.schemeBillHeader) {
      try {
        if (state.addIPDBill.currState.currSchemeBillHeaderId) {
          const documentUrl =
            action.payload.response?.schemeBillHeader.billPathScheme || "";
          const documentUrlPatientPayable =
            action.payload.response?.schemeBillHeader.billPathPatientPayable ||
            "";
          const documentUrlReferralLetter =
            action.payload.response?.schemeBillHeader.s3DocReferralLetter || "";
          const documentUrlDischargeSummary =
            action.payload.response?.schemeBillHeader.s3DocDischargeSummary ||
            "";
          const documentUrlOthers =
            action.payload.response?.schemeBillHeader.s3DocOthers || "";

          const documentUrlDocVariation =
            action.payload.response?.schemeBillHeader
              .billPathVariationStatement || "";

          const uniqueUrl = documentUrl
            ? `${documentUrl}?timestamp=${new Date().getTime()}`
            : "";
          const uniqueUrlPatientPayable = documentUrlPatientPayable
            ? `${documentUrlPatientPayable}?timestamp=${new Date().getTime()}`
            : "";
          const uniqueUrlReferralLetter = documentUrlReferralLetter
            ? `${documentUrlReferralLetter}?timestamp=${new Date().getTime()}`
            : "";
          const uniqueUrlDischargeSummary = documentUrlDischargeSummary
            ? `${documentUrlDischargeSummary}?timestamp=${new Date().getTime()}`
            : "";
          const uniqueUrlDocumentUrlOthers = documentUrlOthers
            ? `${documentUrlOthers}?timestamp=${new Date().getTime()}`
            : "";
          const uniqueUrlDocumentDocVariation = documentUrlDocVariation
            ? `${documentUrlDocVariation}?timestamp=${new Date().getTime()}`
            : "";

          const docVariation: ICompareDocsItems = {
            type: extractFileType(
              action.payload.response?.schemeBillHeader
                .billPathVariationStatement || ""
            ),
            item: uniqueUrlDocumentDocVariation,
            isUploaded: true,
            fileObj: null,
          };
          const docCGHSBill: ICompareDocsItems = {
            type: extractFileType(
              action.payload.response?.schemeBillHeader.billPathScheme || ""
            ),
            item: uniqueUrl,
            isUploaded: true,
            fileObj: null,
          };
          const docPatientPayable: ICompareDocsItems = {
            type: extractFileType(
              action.payload.response?.schemeBillHeader
                .billPathPatientPayable || ""
            ),
            item: uniqueUrlPatientPayable,
            isUploaded: true,
            fileObj: null,
          };
          const docReferralLetter: ICompareDocsItems = {
            type: extractFileType(
              action.payload.response?.schemeBillHeader.s3DocReferralLetter ||
                ""
            ),
            item: uniqueUrlReferralLetter,
            isUploaded: action.payload.response?.schemeBillHeader
              ? true
              : false,
            fileObj: null,
          };
          const docDischargeSumary: ICompareDocsItems = {
            type: extractFileType(
              action.payload.response?.schemeBillHeader.s3DocDischargeSummary ||
                ""
            ),
            item: uniqueUrlDischargeSummary,
            isUploaded: action.payload.response?.schemeBillHeader
              .s3DocDischargeSummary
              ? true
              : false,
            fileObj: null,
          };
          const docOthers: ICompareDocsItems = {
            type: extractFileType(
              action.payload.response?.schemeBillHeader.s3DocOthers || ""
            ),
            item: uniqueUrlDocumentUrlOthers,
            isUploaded: action.payload.response?.schemeBillHeader.s3DocOthers
              ? true
              : false,
            fileObj: null,
          };

          state.addIPDBill.relatedDocs[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ] = {
            ["VARIATION"]: docVariation,
            ["CGHS BILL"]: docCGHSBill,
            ["PATIENT PAYABLE"]: docPatientPayable,
            ["DISCHARGE SUMMARY"]: docDischargeSumary,
            ["REFERRAL LETTER"]: docReferralLetter,
            ["OTHER"]: docOthers,
          };
        }
      } catch (error) {
        console.error("Can't update related docs in store");
      }

      // Populate cghs.ipd
      const cghsIPDBillItems: {
        [key: string]: Array<number>;
      } = {};
      action.payload.response?.schemeBillItems.schemeBillIpdItems?.forEach(
        (ipdItem) => {
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.ipd[ipdItem.id] =
            ipdItem;
          if (
            ipdItem.collection &&
            !cghsIPDBillItems.hasOwnProperty(ipdItem.collection)
          ) {
            cghsIPDBillItems[ipdItem.collection] = [ipdItem.id];
          } else if (ipdItem.collection) {
            cghsIPDBillItems[ipdItem.collection].push(ipdItem.id);
          }

          // Update the mappings in health care bill items
          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "ipd",
            itemMappingInSchemeServiceCategory: ipdItem.serviceCategory,
            itemMappingInSchemeId: ipdItem.id,
            itemMappingInSchemeCollection: ipdItem.collection || "",
          };

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings = [];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings = [];
            }
          });

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings.push(schemeMapping);
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings.push(schemeMapping);
            }
          });
        }
      );

      // Populate cghs.implant
      const cghsImplantBillItems: {
        [key: string]: Array<number>;
      } = {};
      action.payload.response?.schemeBillItems.ipdBillImplantItems?.forEach(
        (ipdItem) => {
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.implant[
            ipdItem.id
          ] = ipdItem;
          if (
            ipdItem.collection &&
            !cghsImplantBillItems.hasOwnProperty(ipdItem.collection)
          ) {
            cghsImplantBillItems[ipdItem.collection] = [ipdItem.id];
          } else if (ipdItem.collection) {
            cghsImplantBillItems[ipdItem.collection].push(ipdItem.id);
          }

          // Update the mappings in health care bill items
          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "implant",
            itemMappingInSchemeServiceCategory: ipdItem.serviceCategory,
            itemMappingInSchemeId: ipdItem.id,
            itemMappingInSchemeCollection: ipdItem.collection || "",
          };

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings = [];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings = [];
            }
          });

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings.push(schemeMapping);
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings.push(schemeMapping);
            }
          });
        }
      );

      // Populate cghs.package
      const cghsPackageBillItems: {
        [key: string]: Array<number>;
      } = {};
      action.payload.response?.schemeBillItems.ipdBillPackageItems?.forEach(
        (ipdItem) => {
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package[
            ipdItem.id
          ] = ipdItem;
          if (
            ipdItem.collection &&
            !cghsPackageBillItems.hasOwnProperty(ipdItem.collection)
          ) {
            cghsPackageBillItems[ipdItem.collection] = [ipdItem.id];
          } else if (ipdItem.collection) {
            cghsPackageBillItems[ipdItem.collection].push(ipdItem.id);
          }

          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "package",
            itemMappingInSchemeServiceCategory: ipdItem.serviceCategory,
            itemMappingInSchemeId: ipdItem.id,
            itemMappingInSchemeCollection: ipdItem.collection || "",
          };

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings = [];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings = [];
            }
          });

          // Update the mappings in health care bill items
          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings.push(schemeMapping);
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings.push(schemeMapping);
            }
          });
        }
      );

      // Populate cghs.medical
      const cghsMedicalBillItems: {
        [key: string]: Array<number>;
      } = {};
      action.payload.response?.schemeBillItems.schemeBillMedicalItems?.forEach(
        (ipdItem) => {
          state.addIPDBill.finalScreen.allSchemeBillItems.cghs.medical[
            ipdItem.id
          ] = ipdItem;
          if (
            ipdItem.collection &&
            !cghsMedicalBillItems.hasOwnProperty(ipdItem.collection)
          ) {
            cghsMedicalBillItems[ipdItem.collection] = [ipdItem.id];
          } else if (ipdItem.collection) {
            cghsMedicalBillItems[ipdItem.collection].push(ipdItem.id);
          }

          // Update the mappings in health care bill items
          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "cghs",
            itemMappingInSchemeSubCategory: "medical",
            itemMappingInSchemeServiceCategory: ipdItem.serviceCategory,
            itemMappingInSchemeId: ipdItem.id,
            itemMappingInSchemeCollection: ipdItem.collection || "",
          };

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings = [];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings = [];
            }
          });

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings.push(schemeMapping);
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings.push(schemeMapping);
            }
          });
        }
      );

      // Populate patientPayable.ipd
      const patientPayableIPDBillItems: {
        [key: string]: Array<number>;
      } = {};
      action.payload.response?.schemeBillItems.schemeBillIpdItemsPatientPayable?.forEach(
        (ipdItem) => {
          state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable.ipd[
            ipdItem.id
          ] = ipdItem;
          if (
            ipdItem.collection &&
            !patientPayableIPDBillItems.hasOwnProperty(ipdItem.collection)
          ) {
            patientPayableIPDBillItems[ipdItem.collection] = [ipdItem.id];
          } else if (ipdItem.collection) {
            patientPayableIPDBillItems[ipdItem.collection].push(ipdItem.id);
          }

          // Update the mappings in health care bill items
          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "patientPayable",
            itemMappingInSchemeSubCategory: "ipd",
            itemMappingInSchemeServiceCategory: ipdItem.serviceCategory,
            itemMappingInSchemeId: ipdItem.id,
            itemMappingInSchemeCollection: ipdItem.collection || "",
          };

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings = [];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings = [];
            }
          });

          ipdItem.lid?.forEach((id) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings.push(schemeMapping);
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings.push(schemeMapping);
            }
          });
        }
      );

      // Populate patientPayable.medical
      const patientPayableMedicalBillItems: {
        [key: string]: Array<number>;
      } = {};
      action.payload.response?.schemeBillItems.schemeBillMedicalItemsPatientPayable?.forEach(
        (ipdItem) => {
          state.addIPDBill.finalScreen.allSchemeBillItems.patientPayable.medical[
            ipdItem.id
          ] = ipdItem;
          if (
            ipdItem.collection &&
            !patientPayableMedicalBillItems.hasOwnProperty(ipdItem.collection)
          ) {
            patientPayableMedicalBillItems[ipdItem.collection] = [ipdItem.id];
          } else if (ipdItem.collection) {
            patientPayableMedicalBillItems[ipdItem.collection].push(ipdItem.id);
          }

          // Update the mappings in health care bill items
          const schemeMapping: ISchemeMappingsInHealthCare = {
            itemMappingInSchemeCategory: "patientPayable",
            itemMappingInSchemeSubCategory: "medical",
            itemMappingInSchemeServiceCategory: ipdItem.serviceCategory,
            itemMappingInSchemeId: ipdItem.id,
            itemMappingInSchemeCollection: ipdItem.collection,
          };

          ipdItem.lid?.forEach((id: number) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings = [];
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings = [];
            }
          });

          ipdItem.lid?.forEach((id: number) => {
            if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
                id
              ].schemeMappings.push(schemeMapping);
            } else if (
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
                id
              )
            ) {
              state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                id
              ].schemeMappings.push(schemeMapping);
            }
          });
        }
      );

      //Populate collections
      const collections =
        action.payload.response?.schemeBillItems?.collectionsList;

      state.addIPDBill.finalScreen.collections = collections;

      state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[schemeHeaderId] = {
        billHeader: action.payload.response?.schemeBillHeader,
        billItems: {
          cghs: {
            ipd: cghsIPDBillItems,
            implant: cghsImplantBillItems,
            medical: cghsMedicalBillItems,
            package: cghsPackageBillItems,
          },
          patientPayable: {
            ipd: patientPayableIPDBillItems,
            medical: patientPayableMedicalBillItems,
          },
        },
      };
    }
  }
};

const updateIPDBillMappingsUtil = (
  state: IIPDBillsState,
  action: PayloadAction<IIPDBillMappingFetchResp | IIPDErrorResp>,
  editFlow: boolean
) => {
  const { response } = action.payload;
  if (response) {
    state.addIPDBill.currState.currBillMappingId = response.billMapping.id;

    state.addIPDBill.currState.currHealthCareBillHeaderId =
      response.billMapping.healthCareBillHeaderId;

    // Set healthCareBillIdDataMap to null
    state.addIPDBill.finalScreen.healthCareBillIdDataMap[
      response.billMapping.healthCareBillHeaderId
    ] = {
      billHeader: null,
      billItems: {
        ipd: {},
        medical: {},
      },
      patientPayableData: null,
    };

    // Set schemeBillHeaderIdToSchemeBillHeaderDataMap to null
    state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
      response.billMapping.schemeBillHeaderId
    ] = {
      billHeader: null,
      billItems: {
        cghs: {
          implant: {},
          ipd: {},
          package: {},
          medical: {},
        },
        patientPayable: {
          ipd: {},
          medical: {},
        },
      },
    };

    state.addIPDBill.currState.currSchemeBillHeaderId =
      response.billMapping.schemeBillHeaderId;

    state.addIPDBill.finalScreen.billMappingMap[response.billMapping.id] = {
      billMapping: response.billMapping,
      documentPaths: response.documentPaths,
    };

    if (editFlow) {
      state.addIPDBill.currState.claimId =
        response.billMapping.claimId?.toString();
      // For edit flow update local files variable from response
      response.documentPaths?.forEach((doc) => {
        state.addIPDBill.localFiles[doc.documentType] = {
          ...doc,
          originFileObj: {},
          caseId: "",
          hospitalId: "",
          patientId: "",
          uid: doc.s3DocumentId,
          name: doc.documentName,
          url: doc.documentUrl,
        };
        state.addIPDBill.localFiles2.push({
          ...doc,
          originFileObj: {}, // Initialize empty object for the file object
          caseId: "",
          hospitalId: "",
          patientId: "",
          uid: doc.s3DocumentId,
          name: doc.documentName,
          url: doc.documentUrl,
        });
      });

      // For edit flow in wizard canNavigate and complete should be true
      state.addIPDBill.allWizardSteps?.forEach((step) => {
        step.completed = true;
        step.canNavigate = true;
      });

      // For edit flow the flow should directly land on final screen
      state.addIPDBill.currState.wizardCurrScreen =
        state.addIPDBill.allWizardSteps.length - 1;
    }
    if (response?.billMapping?.status == "Unable to processes file") {
      errorMessage(response?.billMapping?.status, 4);
    }
  }
};

const resetIPDBillStateUtil = (state: IIPDBillsState) => {
  state.isFetchingBillList = false;
  state.pageToIPDBillList = {};
  state.ipdListFilters = {
    searchTerm: "",
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: 10,
    startDate: null,
    endDate: null,
  };
  state.ipdBillListCurrState = {
    totalElements: 0,
  };
  state.addIPDBill = {
    filters: {
      searchFiltersByComponents: {
        scheme: {
          cghs: {
            ipd: "",
            implant: "",
            package: "",
            medical: "",
          },
          patientPayable: {
            ipd: "",
            medical: "",
          },
        },
        healthCare: {
          ipd: "",
          medical: "",
        },
      },
    },
    localFiles: {},
    localFiles2: [],

    allWizardSteps: ADD_IPD_BILL_WIZARD_ITEMS,

    ipdManualQuestions: {
      ipdQuestionnaires: {},
      isFetchingQuestionnaires: false,
      currentQuestionId: 1,
      answeredQuestionsResponse: {
        cardCategory: "",
        conclusion: "",
        healthCareBillItemsResponses: {
          healthCareBillIpdItems: [],
          healthCareBillMedicalItems: [],
        },
        packageDates: [],
        questionAnswersCollectedResponses: [],
        tataMemorialRateApply: "",
      },
      isConclusionGenerated: false,
    },
    finalScreen: {
      healthCareBillIdDataMap: {},
      schemeBillHeaderIdDataMap: {},
      collections: [],
      billMappingMap: {},
      allHealthCareBillItems: {
        ipd: {},
        medical: {},
      },
      allSchemeBillItems: {
        cghs: {
          implant: {},
          ipd: {},
          package: {},
          medical: {},
        },
        patientPayable: {
          ipd: {},
          medical: {},
        },
      },
    },
    billMappings: null,
    errors: {
      errorBillMapping: null,
      errorHealthCareBillHeader: null,
      errorHealthCarePatientPayable: null,
      errorQuestionAnswerCollection: null,
      errorBillSchemeHeader: null,
      errorPostQuestionAnswerCollection: null,
      errorInUpdatingSchemeBillItems: null,
      errorInApprovingIPDBill: null,
    },
    loaders: {
      isSchemeBillItemsUpdating: false,
      isFetchIPDBillData: false,
      isApprovingIPDBill: false,
      isResetBillItems: false,
    },
    currState: {
      currBillMappingId: null,
      currSchemeBillHeaderId: null,
      currHealthCareBillHeaderId: null,
      wizardCurrScreen: 0,
      claimId: null,
      arePackagedItemsHighlighted: false,
      arePatientPayableItemsHighlighted: false,
    },
    relatedDocs: {},
  };
};

const remove1to1IPDSchemeBillItemUtil = (
  state: IIPDBillsState,
  lidList: Array<number>
) => {};

const updateSchemePackageUtil = (
  state: IIPDBillsState,
  action: PayloadAction<{ packages: Array<IIPDSchemeBillPackageItem> }>
) => {
  try {
    const { packages } = action.payload;
    if (
      state.addIPDBill.currState.currSchemeBillHeaderId !== null &&
      packages.length > 0
    ) {
      packages?.forEach((packageItem) => {
        state.addIPDBill.finalScreen.allSchemeBillItems.cghs.package[
          packageItem.id
        ] = packageItem;

        const schemeMapping: ISchemeMappingsInHealthCare = {
          itemMappingInSchemeCategory: "cghs",
          itemMappingInSchemeSubCategory: "package",
          itemMappingInSchemeServiceCategory: packageItem.serviceCategory,
          itemMappingInSchemeId: packageItem.id,
          itemMappingInSchemeCollection: packageItem.collection || "",
        };

        packageItem.lid?.forEach((lid) => {
          //check if item is present in healthcare ipd
          if (
            state.addIPDBill.finalScreen.allHealthCareBillItems.ipd.hasOwnProperty(
              lid
            )
          ) {
            state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[
              lid
            ].schemeMappings = [
              ...state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[lid]
                .schemeMappings,
              schemeMapping,
            ];
          } else if (
            state.addIPDBill.finalScreen.allHealthCareBillItems.medical.hasOwnProperty(
              lid
            )
          ) {
            state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
              lid
            ].schemeMappings = [
              ...state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
                lid
              ].schemeMappings,
              schemeMapping,
            ];
          }
        });
      });

      if (
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billItems.cghs.package.hasOwnProperty("PACKAGE") &&
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billItems.cghs.package["PACKAGE"]
      ) {
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billItems.cghs.package["PACKAGE"] = [
          ...state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
            state.addIPDBill.currState.currSchemeBillHeaderId
          ].billItems.cghs.package["PACKAGE"],
          ...packages?.map((packageItem) => {
            return packageItem.id;
          }),
        ];
      } else {
        state.addIPDBill.finalScreen.schemeBillHeaderIdDataMap[
          state.addIPDBill.currState.currSchemeBillHeaderId
        ].billItems.cghs.package["PACKAGE"] = [
          ...packages?.map((packageItem) => {
            return packageItem.id;
          }),
        ];
      }
    }
    successMessage("Package added successfully");
  } catch (error) {
    errorMessage("Error in adding package");
    console.error(error);
  }
};

const fetchNewIPDHealthCareBillHeaderUtil = (
  state: IIPDBillsState,
  action: PayloadAction<IIPDHealthCareBillHeaderFetchResp | IIPDErrorResp>
) => {
  try {
    const healthCareBillHeaderId =
      action.payload.response?.healthCareBillHeader.id;

    const headerData = action.payload.response?.healthCareBillHeader;

    const healthCareBillItems = action.payload.response?.healthCareBillItems;

    if (healthCareBillHeaderId && headerData && healthCareBillItems) {
      const tempHealthCareIPDBillItemsMap: {
        [key: string]: Array<number>;
      } = {};

      healthCareBillItems.healthCareBillIpdItems?.forEach((ipdItem) => {
        // fill all healthcare bill items map
        state.addIPDBill.finalScreen.allHealthCareBillItems.ipd[ipdItem.id] = {
          ...ipdItem,
          schemeMappings: [],
        };

        // Populate Health Care IPD Bill Items Map
        if (
          !tempHealthCareIPDBillItemsMap.hasOwnProperty(ipdItem.serviceCategory)
        ) {
          tempHealthCareIPDBillItemsMap[`${ipdItem.serviceCategory}`] = [
            ipdItem.id,
          ];
        } else {
          tempHealthCareIPDBillItemsMap[`${ipdItem.serviceCategory}`].push(
            ipdItem.id
          );
        }
      });

      const tempHealthCareMedicalBillItemsMap: {
        [key: string]: Array<number>;
      } = {};
      healthCareBillItems.healthCareBillMedicalItems?.forEach((medicalItem) => {
        // fill all healthcare bill items map
        state.addIPDBill.finalScreen.allHealthCareBillItems.medical[
          medicalItem.id
        ] = {
          ...medicalItem,
          schemeMappings: [],
        };

        // Populate Health Care Medical Bill Items Map
        if (
          !tempHealthCareMedicalBillItemsMap.hasOwnProperty(
            medicalItem.serviceCategory
          )
        ) {
          tempHealthCareMedicalBillItemsMap[`${medicalItem.serviceCategory}`] =
            [medicalItem.id];
        } else {
          tempHealthCareMedicalBillItemsMap[
            `${medicalItem.serviceCategory}`
          ].push(medicalItem.id);
        }
      });

      state.addIPDBill.finalScreen.healthCareBillIdDataMap[
        healthCareBillHeaderId
      ] = {
        ...state.addIPDBill.finalScreen.healthCareBillIdDataMap[
          healthCareBillHeaderId
        ],
        billHeader: headerData,
        billItems: {
          ipd: tempHealthCareIPDBillItemsMap,
          medical: tempHealthCareMedicalBillItemsMap,
        },
      };
    }
  } catch (error) {
    state.addIPDBill.errors.errorHealthCareBillHeader =
      "Cannot fetch health care bill headers";
  } finally {
    state.addIPDBill.loaders.isFetchIPDBillData = false;
  }
};

export {
  updateSchemeDataUtil,
  updateIPDBillMappingsUtil,
  resetIPDBillStateUtil,
  remove1to1IPDSchemeBillItemUtil,
  updateSchemePackageUtil,
  fetchNewIPDHealthCareBillHeaderUtil,
};
